/**
 * RouteDetails copy — English only.
 *
 * New `RouteDetails.*` keys per QA spec §11-Q2 (Figma redesign copy,
 * en-only, byte-identical across iOS `RouteDetails/Localizable.xcstrings`,
 * Android `strings.xml`, and this file). The Figma wording diverges from
 * the V1 `RouteDetails.*` keys ("Tap in" vs "Tap in at gates",
 * "Walk to the platform" vs "Enter station", "Make a transfer" vs
 * "Transfer", "%d stations" vs "%d stops"); the lead resolution is to
 * ship the Figma copy as the V2 contract. Localisation to other locales
 * is a separate follow-up, not P1.
 *
 * Mirrors the schema-agnostic pattern of the rest of the web modules:
 * plain string constants + tiny formatters, no i18n framework (the app
 * has none yet — same as RouteCard / Suggester).
 *
 * TODO(task #5 — web RouteDetails localization): these labels are
 * en-only constants with NO live locale layer, so every non-base-en web
 * user (en-GB, en-IN, zh-Hans/Hant/HK, all 87 catalog locales) sees the
 * base-en wording, diverging from iOS which serves each locale its own
 * translation. iOS (@c438dfbbf) splits English three ways — en "first
 * car" / en-GB "first carriage" / en-IN "first coach"; en "...best
 * transfer spots" vs en-GB/en-IN "...the best..." — plus full zh + 87
 * locales. The public/locales/<locale>/route-details.json files exist
 * but are dead (nothing reads them via i18next t()). Closing the gap means
 * wiring these components to i18next (or an en-GB/en-IN/zh-aware
 * selector) across the full locale set + tests; it touches the phone
 * path too, so it is a follow-up, OUT of TASK-412 scope (lead call,
 * 2026-06-08). Base-en here is byte-identical to the iOS canonical en.
 */
/**
 * The label set for the RouteDetails surface. The host builds a
 * localised instance from the `route-details` catalog via
 * `buildRouteDetailsStrings(t)` (see `src/i18n/packageStrings.ts`) and
 * threads it through the section builder + row components. The English
 * {@link RouteDetailsStrings} below is the default, byte-identical to
 * the shipped en catalog, so an un-threaded / provider-less render is
 * unchanged.
 *
 * `stations` is the pluralised station-count template ("N station(s)");
 * `towards` is the "Towards <name>" template. They are stored as small
 * formatter functions so the consumer stays format-agnostic.
 */
export interface RouteDetailsStringSet {
  readonly tapIn: string
  readonly tapOut: string
  readonly walkToThePlatform: string
  readonly walkToTheExit: string
  readonly makeATransfer: string
  readonly upsellTitle: string
  readonly reportIssue: string
  readonly forTransferPrefix: string
  readonly carFirst: string
  readonly carNearFirst: string
  readonly carMiddle: string
  readonly carNearEnd: string
  readonly carLast: string
  readonly carAll: string
  readonly carOr: string
  /** "<line_id> line" fallback subtitle suffix for an unresolved line. */
  readonly lineSuffix: string
  /** Pluralised "N station(s)" expand-row title. */
  readonly stations: (count: number) => string
  /** "Towards <name>" direction-row title. */
  readonly towards: (stationName: string) => string
  /**
   * Street-walk row title — "Walk to <station> (<line>)", dropping the
   * parenthetical when the target station's line can't be resolved
   * (2026-07 street-walk contract).
   */
  readonly walkToStation: (stationName: string, lineLabel: string | null) => string
  /** MapRow accessibility label — "Map of <station>". aria-only. */
  readonly mapOf: (stationName: string) => string
}

export const RouteDetailsStrings: RouteDetailsStringSet = {
  /** ValidationRow, type = entry. */
  tapIn: 'Tap in',
  /** ValidationRow, type = exit. */
  tapOut: 'Tap out',
  /** EntryRow, type = entry (figure.walk into the station). */
  walkToThePlatform: 'Walk to the platform',
  /** EntryRow, type = exit (figure.walk out of the station). */
  walkToTheExit: 'Walk to the exit',
  /** TransferRow label between two line blocks. */
  makeATransfer: 'Make a transfer',
  /** UpsellBanner title (non-premium gate). */
  upsellTitle: 'Get Premium to see best transfer spots',
  /** ReportIssueRow label. */
  reportIssue: 'Report an issue',
  /** PossibleTransfer caption prefix. Joined with the car fragment(s). */
  forTransferPrefix: 'For transfer use',
  /** PossibleTransfer car fragments — match V1 `RouteDetails.Transfer.*`. */
  carFirst: 'first car',
  carNearFirst: 'near first car',
  carMiddle: 'middle of the train',
  carNearEnd: 'near end car',
  carLast: 'last car',
  carAll: 'all cars',
  /** PossibleTransfer joiner between two car fragments. */
  carOr: ' or ',
  lineSuffix: ' line',
  stations: (count: number) => `${count} ${count === 1 ? 'station' : 'stations'}`,
  towards: (stationName: string) => `Towards ${stationName}`,
  walkToStation: (stationName: string, lineLabel: string | null) =>
    lineLabel === null ? `Walk to ${stationName}` : `Walk to ${stationName} (${lineLabel})`,
  mapOf: (stationName: string) => `Map of ${stationName}`,
}

/**
 * Expand-row title — pluralised "N stations" (Figma copy; V1 used
 * "%d stop(s)"). Singular/plural both render "station(s)".
 *
 * `strings` defaults to the English {@link RouteDetailsStrings}; the
 * host passes a localised set.
 */
export function stationsCountLabel(
  count: number,
  strings: RouteDetailsStringSet = RouteDetailsStrings,
): string {
  return strings.stations(count)
}

/**
 * Direction-row title — "Towards <name>" (Figma "Towards %@", dropping
 * V1's "station" word). The name binding stays the V1 derivation (the
 * second stop of the line block), per §11-Q3.
 */
export function towardsLabel(
  stationName: string,
  strings: RouteDetailsStringSet = RouteDetailsStrings,
): string {
  return strings.towards(stationName)
}

/**
 * FirstStop subtitle — "<line> line" (Figma copy, e.g. "Central line").
 * `LineDisplay.localName` already reads "Central line" in the web schema,
 * so this returns it verbatim; kept as a function so the contract is
 * explicit and a future raw-short-name schema can be adapted in one place.
 *
 * The line **name** is schema data, not translatable copy — only the
 * unresolved-fallback " line" suffix (built in `sections.ts`) is.
 */
export function lineSubtitle(lineLocalName: string): string {
  return lineLocalName
}
